import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useEventCallback } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';

import { EntityPromotionsResponse, PromotionComplete, PromotionFeatures } from '@playq/octopus-common';

import { QUERY_PROMOTIONS_TIMEOUT, useQueryPromotions } from '/api/hooks/promotions';
import { activeSpaceToolkit } from '/store/toolkits';
import { getSandboxPromotion, isPromotionLoading } from '/shared/Promotions/helpers';
import { EntityID } from '/shared/Promotions/components/Service/types';
import { spacePromotionsKey } from '/api';

import { PromotionsPreview } from './PromotionsPreview';
import { IControlBarPromotionPreviewProps } from './types';
import { ControlBarWrapper } from './styles';

const features = [PromotionFeatures.SpaceName];

export const ControlBarPromotionsPreview = <ID extends EntityID>({
  version,
  service,
  entityID,
  spaceless,
  disabled,
  onChange,
  setIsPromotedOnLive,
  lastAvailableVersion,
  ...props
}: IControlBarPromotionPreviewProps<ID>) => {
  const activeSpace = useSelector(activeSpaceToolkit.selectors.activeSpace);
  const space = spaceless ? undefined : activeSpace;

  const isMutating = useIsMutating({
    mutationKey: spacePromotionsKey,
  });

  const queryVersions = useMemo(() => service.queryPromotions.bind(service), [service]);
  const { data, refetch, isLoading } = useQueryPromotions(entityID, features, queryVersions);

  const [promotions, setPromotions] = useState<EntityPromotionsResponse | undefined>(data);
  const promotionTimerID = useRef<number | undefined>();
  const shouldRefetchImmediatelly = useRef(false);

  const isPromotedOnLive: boolean = useMemo(() => {
    const live = data?.live;
    if (!live || version === undefined) {
      return false;
    }
    if (!(live.state instanceof PromotionComplete)) {
      return false;
    }
    return live.version === version;
  }, [data?.live, version]);

  useEffect(() => {
    if (setIsPromotedOnLive) {
      setIsPromotedOnLive(isPromotedOnLive);
    }
    // passing isPromotedOnLive to deps can cause too many re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPromotedOnLive]);

  useEffect(() => {
    setPromotions(data);
  }, [data]);

  const handleChange = useEventCallback((newPromotions: EntityPromotionsResponse | undefined) => {
    setPromotions(newPromotions);
    onChange?.(newPromotions);
    const newLiveVersion = newPromotions?.live?.version;
    if (newLiveVersion !== undefined && newLiveVersion === version) {
      setIsPromotedOnLive?.(true);
    }
  });

  useEffect(
    function refetchAfterSpacePromotionMutation() {
      if (isMutating > 0) {
        shouldRefetchImmediatelly.current = true;
        return;
      }
      if (shouldRefetchImmediatelly.current) {
        refetch();
        shouldRefetchImmediatelly.current = false;
      }
    },
    [isMutating, refetch]
  );

  useEffect(() => {
    if (isPromotionLoading(promotions?.live) || isPromotionLoading(getSandboxPromotion(promotions, space))) {
      promotionTimerID.current = window.setTimeout(() => {
        refetch();
      }, QUERY_PROMOTIONS_TIMEOUT);
    }
    return () => {
      window.clearTimeout(promotionTimerID.current);
    };
  }, [promotions, space, refetch]);

  return (
    <ControlBarWrapper>
      <PromotionsPreview<ID>
        {...props}
        promotions={promotions}
        version={version}
        service={service}
        entityID={entityID}
        previewButtonProps={{ withVersion: true }}
        disabled={disabled || isLoading}
        onChange={handleChange}
        lastAvailableVersion={lastAvailableVersion}
        spaceless={spaceless}
      />
    </ControlBarWrapper>
  );
};
